import React, { useCallback, useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { useAuth } from '../../contexts/AuthContext'
import { db, functions } from '../../helpers/firebase'
import { newerExerciseTopic, parentInviteShareText } from '../../helpers/parentInviteShare'
import { cacheParentInvite, readCachedParentInvite } from '../../helpers/parentInviteStorage'
import './ParentInvitePanel.css'

const INVITE_PANEL_COPY = 'Share this link with your parent so they can sign up and give your account unlimited access. This link expires in 7 days and works once.'

const inviteUrl = (urlPath) => `${window.location.origin}${urlPath}`

let inviteRequest = null

const requestParentInvite = async (force) => {
  if (!force) {
    const cached = readCachedParentInvite()
    if (cached) return cached
    if (inviteRequest) return inviteRequest
  }
  inviteRequest = (async () => {
    const createParentInvite = httpsCallable(functions, 'createParentInvite')
    const result = await createParentInvite({})
    const next = {
      urlPath: result.data.urlPath,
      expiresAt: result.data.expiresAt
    }
    cacheParentInvite(next)
    return next
  })()
  const request = inviteRequest
  try {
    return await request
  } finally {
    if (inviteRequest === request) inviteRequest = null
  }
}

const callableMessage = (error, fallback) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : ''
  return message || fallback
}

// Check Answer documents store timestamp. Explain sessions store createdAt.
// Ordering Check Answer by createdAt skips every saved exercise.
const latestExercise = async (uid, collectionName, timeField) => {
  const snap = await getDocs(query(
    collection(db, 'users', uid, collectionName),
    orderBy(timeField, 'desc'),
    limit(1)
  ))
  if (snap.empty) return null
  const data = snap.docs[0].data() || {}
  return {
    createdAt: data[timeField],
    topic: data.topic
  }
}

// QR, link, Copy, and Share for one active parent invite. A new create
// replaces the code so a revoked link is never left on screen.
const ParentInvitePanel = () => {
  const { currentUser } = useAuth()
  const studentUid = currentUser && currentUser.uid ? currentUser.uid : ''
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [canShare, setCanShare] = useState(false)
  const [shareTopic, setShareTopic] = useState('')
  const [topicReady, setTopicReady] = useState(false)

  const createInvite = useCallback(async (force = false) => {
    setLoading(true)
    setError('')
    setCopied(false)
    try {
      const next = await requestParentInvite(force)
      setInvite(next)
    } catch (createError) {
      setError(callableMessage(createError, 'Could not create an invite link. Please try again.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function')
    createInvite(false)
  }, [createInvite])

  useEffect(() => {
    let cancelled = false
    if (!studentUid) {
      setShareTopic('')
      setTopicReady(true)
      return undefined
    }
    setTopicReady(false)
    const loadTopic = async () => {
      try {
        const [checkAnswer, explain] = await Promise.all([
          latestExercise(studentUid, 'studyQuestions', 'timestamp'),
          latestExercise(studentUid, 'explainSessions', 'createdAt')
        ])
        if (!cancelled) setShareTopic(newerExerciseTopic(checkAnswer, explain))
      } catch (readError) {
        if (!cancelled) setShareTopic('')
      } finally {
        if (!cancelled) setTopicReady(true)
      }
    }
    loadTopic()
    return () => {
      cancelled = true
    }
  }, [studentUid])

  const url = invite ? inviteUrl(invite.urlPath) : ''
  const shareText = parentInviteShareText(shareTopic)

  const copyLink = async () => {
    if (!url || !topicReady) return
    try {
      await navigator.clipboard.writeText(`${shareText}\n${url}`)
      setCopied(true)
    } catch (copyError) {
      setError('Could not copy the link. Select it and copy it manually.')
    }
  }

  const shareLink = async () => {
    if (!url || !topicReady || !navigator.share) return
    try {
      await navigator.share({
        title: 'Quillnote',
        text: shareText,
        url
      })
    } catch (shareError) {
      if (shareError && shareError.name === 'AbortError') return
      setError('Could not open the share sheet.')
    }
  }

  return (
    <div className='parent-invite-panel'>
      <h3 className='parent-invite-title'>Invite a parent</h3>
      {loading && <p className='parent-invite-status'>Creating a link...</p>}
      {error && <p className='parent-invite-error' role='alert'>{error}</p>}
      {invite && !loading && (
        <>
          <div className='parent-invite-qr' aria-hidden='true'>
            <QRCode value={url} size={220} />
          </div>
          <p className='parent-invite-url'>{url}</p>
          <div className='parent-invite-actions'>
            {canShare && (
              <button type='button' className='parent-invite-primary' onClick={shareLink} disabled={!topicReady}>
                Share
              </button>
            )}
            <button
              type='button'
              className={canShare ? 'parent-invite-secondary' : 'parent-invite-primary'}
              onClick={copyLink}
              disabled={!topicReady}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type='button' className='parent-invite-text' onClick={() => createInvite(true)} disabled={loading}>
              Get a new link
            </button>
          </div>
        </>
      )}
      <p className='parent-invite-copy'>
        {INVITE_PANEL_COPY}
      </p>
    </div>
  )
}

export default ParentInvitePanel
